/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpMetadataRequest,
  GetMcpMetadataSecurity,
} from "../models/operations/getmcpmetadata.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMcpMetadataQuery,
  GetMcpMetadataQueryData,
  prefetchGetMcpMetadata,
  queryKeyGetMcpMetadata,
} from "./getMcpMetadata.core.js";
export {
  buildGetMcpMetadataQuery,
  type GetMcpMetadataQueryData,
  prefetchGetMcpMetadata,
  queryKeyGetMcpMetadata,
};

export type GetMcpMetadataQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMcpMetadata mcpMetadata
 *
 * @remarks
 * Fetch the metadata that powers the MCP install page. Exactly one of toolset_slug or mcp_server_id must be provided.
 */
export function useGetMcpMetadata(
  request?: GetMcpMetadataRequest | undefined,
  security?: GetMcpMetadataSecurity | undefined,
  options?: QueryHookOptions<GetMcpMetadataQueryData, GetMcpMetadataQueryError>,
): UseQueryResult<GetMcpMetadataQueryData, GetMcpMetadataQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMcpMetadataQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMcpMetadata mcpMetadata
 *
 * @remarks
 * Fetch the metadata that powers the MCP install page. Exactly one of toolset_slug or mcp_server_id must be provided.
 */
export function useGetMcpMetadataSuspense(
  request?: GetMcpMetadataRequest | undefined,
  security?: GetMcpMetadataSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMcpMetadataQueryData,
    GetMcpMetadataQueryError
  >,
): UseSuspenseQueryResult<GetMcpMetadataQueryData, GetMcpMetadataQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMcpMetadataQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMcpMetadataData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      toolsetSlug?: string | undefined;
      mcpServerId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetMcpMetadataQueryData,
): GetMcpMetadataQueryData | undefined {
  const key = queryKeyGetMcpMetadata(...queryKeyBase);

  return client.setQueryData<GetMcpMetadataQueryData>(key, data);
}

export function invalidateGetMcpMetadata(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      toolsetSlug?: string | undefined;
      mcpServerId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpMetadata", "get", ...queryKeyBase],
  });
}

export function invalidateAllGetMcpMetadata(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpMetadata", "get"],
  });
}
